import { Component } from '@theme/component';
import { morph } from '@theme/morph';

const SECTION_ID = 'section-rendering-product-card';

class ColorSwatches extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const productUrl = input.dataset.productUrl;
    if (!productUrl) return;

    const currentCheckedInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[data-current-checked]')
    );

    for (const sibling of currentCheckedInputs) {
      sibling.dataset.currentChecked = sibling === input ? 'true' : 'false';
    }

    const card = this.closest('product-card');
    if (card) {
      this.#swapCard(card, productUrl);
    } else {
      window.location.href = productUrl;
    }
  };

  /**
   * Fetches the sibling product's card section and applies the result to the
   * surrounding <product-card> element by morphing each known piece in place.
   * @param {Element} card
   * @param {string} productUrl
   */
  async #swapCard(card, productUrl) {

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url.href, { signal: this.#abortController.signal });
      if (!response.ok) {
        console.warn(`[color-swatches] Failed to fetch product card (${response.status})`, url.href);
        return;
      }
      const html = await response.text();
      const newCard = new DOMParser().parseFromString(html, 'text/html').querySelector('product-card');
      if (!newCard) return;

      applyCardSwap(card, newCard);
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
  }
}

/**
 * Updates each replaceable piece of the live card with content from the
 * fetched response. Per-element so the live card's overlay-link layout stays intact.
 * @param {Element} liveCard
 * @param {Element} newCard
 */
function applyCardSwap(liveCard, newCard) {
  const newHref = newCard.querySelector('[ref="productCardLink"]')?.getAttribute('href');
  if (newHref) {
    for (const link of liveCard.querySelectorAll('[ref="productCardLink"], [ref="cardGalleryLink"], [ref="productTitleLink"]')) {
      link.setAttribute('href', newHref);
    }
  }

  const newTitle = newCard.querySelector('[data-card-title]')?.textContent;
  if (newTitle) {
    const liveTitle = liveCard.querySelector('[ref="productTitleLink"] [role="heading"], [ref="productTitleLink"]');
    if (liveTitle) liveTitle.textContent = newTitle;
  }

  const newImg = newCard.querySelector('img[data-card-image]');
  if (newImg) {
    for (const liveImg of liveCard.querySelectorAll('[ref="cardGallery"] img')) {
      liveImg.setAttribute('src', newImg.getAttribute('src') ?? '');
      liveImg.setAttribute('srcset', newImg.getAttribute('srcset') ?? '');
      liveImg.setAttribute('alt', newImg.getAttribute('alt') ?? '');
    }
  }

  morphIfBoth(liveCard.querySelector('product-price'), newCard.querySelector('product-price'));
  morphIfBoth(liveCard.querySelector('product-sku-component'), newCard.querySelector('product-sku-component'));
}

/**
 * @param {Element | null | undefined} target
 * @param {Element | null | undefined} source
 */
function morphIfBoth(target, source) {
  if (target && source) morph(target, source);
}

if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}
